// -----------------------------------------------------------
// FloraIQ - LADM Concept Matrix (Aba 2: Matriz LADM-FloraIQ)
// -----------------------------------------------------------

import { type FC } from "react";
import { ladmMatrixRows } from "@/mocks/ladm";
import LadmStatusBadge from "./LadmStatusBadge";
import type { LadmBadgeVariant } from "./LadmStatusBadge";

const matrixStatusMap: Record<string, LadmBadgeVariant> = {
  implantado: "implantado",
  parcial: "parcial",
  pendente: "pendente",
};

interface ConceptNodeData {
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const conceptNodes: ConceptNodeData[] = [
  { label: "Party", sublabel: "Partes", icon: "ri-group-line", color: "text-blue-700", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  { label: "RRR", sublabel: "Responsabilidades", icon: "ri-shield-check-line", color: "text-orange-700", bgColor: "bg-orange-50", borderColor: "border-orange-200" },
  { label: "Spatial Unit", sublabel: "Unidade Espacial", icon: "ri-map-pin-line", color: "text-green-700", bgColor: "bg-green-50", borderColor: "border-green-200" },
  { label: "Source", sublabel: "Fontes", icon: "ri-folder-info-line", color: "text-indigo-700", bgColor: "bg-indigo-50", borderColor: "border-indigo-200" },
  { label: "Versioned Object", sublabel: "Histórico", icon: "ri-history-line", color: "text-teal-700", bgColor: "bg-teal-50", borderColor: "border-teal-200" },
];

const LadmConceptMatrix: FC = () => {
  return (
    <div className="space-y-6">
      {/* Mapa Conceitual */}
      <div className="bg-white rounded-card border border-background-200/70 p-6">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-5 text-center">Mapa Conceitual LADM-FloraIQ</h3>
        
        {/* Desktop concept map */}
        <div className="hidden md:flex items-center justify-center gap-0 py-6">
          {conceptNodes.map((node, i, arr) => (
            <div key={node.label} className="flex items-center">
              <div className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${node.borderColor} ${node.bgColor} min-w-[130px] transition-all duration-200 hover:shadow-sm cursor-pointer`}>
                <div className={`w-10 h-10 rounded-lg ${node.bgColor} flex items-center justify-center border ${node.borderColor}`}>
                  <i className={`${node.icon} ${node.color} text-lg`}></i>
                </div>
                <span className={`text-sm font-bold ${node.color} font-heading`}>{node.label}</span>
                <span className="text-xs text-foreground-500">{node.sublabel}</span>
              </div>
              {i < arr.length - 1 && (
                <div className="flex flex-col items-center mx-0.5">
                  <div className="w-8 h-0.5 bg-background-300 rounded-full"></div>
                  <div className="flex items-center">
                    <div className="w-1 h-1 rounded-full bg-background-300"></div>
                    <i className="ri-arrow-right-s-line text-background-400 text-xs"></i>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile concept map — vertical */}
        <div className="md:hidden flex flex-col items-center gap-0 py-4">
          {conceptNodes.map((node, i, arr) => (
            <div key={node.label} className="flex flex-col items-center">
              <div className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${node.borderColor} ${node.bgColor} w-full max-w-[200px] transition-all duration-200`}>
                <div className={`w-10 h-10 rounded-lg ${node.bgColor} flex items-center justify-center border ${node.borderColor}`}>
                  <i className={`${node.icon} ${node.color} text-lg`}></i>
                </div>
                <span className={`text-sm font-bold ${node.color} font-heading`}>{node.label}</span>
                <span className="text-xs text-foreground-500">{node.sublabel}</span>
              </div>
              {i < arr.length - 1 && (
                <div className="flex flex-col items-center py-1">
                  <i className="ri-arrow-down-s-line text-background-400"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-foreground-400 text-center mt-4">
          Party → RRR → Spatial Unit → Source → Versioned Object (Histórico)
        </p>
      </div>

      {/* Tabela Matriz */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/50">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading">Matriz Comparativa LADM × FloraIQ</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100/70">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Conceito LADM</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Adaptação FloraIQ</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Exemplo Prático</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Módulo Relacionado</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {ladmMatrixRows.map((row, i) => (
                <tr key={i} className="hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                        row.conceitoLadm.startsWith("Party") ? "bg-blue-50" :
                        row.conceitoLadm.startsWith("Spatial") ? "bg-green-50" :
                        row.conceitoLadm.startsWith("RRR") ? "bg-orange-50" :
                        row.conceitoLadm.startsWith("Source") ? "bg-indigo-50" :
                        row.conceitoLadm.startsWith("Admin") ? "bg-yellow-50" :
                        row.conceitoLadm.startsWith("Version") ? "bg-teal-50" : "bg-blue-50"
                      }`}>
                        <i className={`${
                          row.conceitoLadm.startsWith("Party") ? "ri-group-line text-blue-600" :
                          row.conceitoLadm.startsWith("Spatial") ? "ri-map-pin-line text-green-600" :
                          row.conceitoLadm.startsWith("RRR") ? "ri-shield-check-line text-orange-600" :
                          row.conceitoLadm.startsWith("Source") ? "ri-folder-info-line text-indigo-600" :
                          row.conceitoLadm.startsWith("Admin") ? "ri-file-list-3-line text-yellow-600" :
                          row.conceitoLadm.startsWith("Version") ? "ri-history-line text-teal-600" : "ri-flight-takeoff-line text-blue-600"
                        } text-xs`}></i>
                      </div>
                      <span className="font-semibold text-foreground-900 whitespace-nowrap">{row.conceitoLadm}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-600 text-xs leading-relaxed max-w-[280px]">{row.adaptacaoFloraIQ}</td>
                  <td className="px-4 py-3 text-foreground-600 text-xs leading-relaxed max-w-[280px]">{row.exemploPratico}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium text-blue-600 whitespace-nowrap">{row.moduloRelacionado}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <LadmStatusBadge variant={matrixStatusMap[row.statusImplantacao] || "pendente"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LadmConceptMatrix;